import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Clock,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  XCircle,
  Eye,
  Download,
  Copy,
  Trash2,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Task, TaskStatus, formatDate, getVideoCount, getTaskThumbnail } from "@/types/task";

interface TaskCardProps {
  task: Task;
  onDelete: (id: string) => void;
  onDuplicate: (task: Task) => void;
}

const statusConfig: Record<
  string,
  { label: string; color: string; icon: React.ComponentType<{ className?: string }> }
> = {
  pending: {
    label: "Pendente",
    color: "text-blue-400 bg-blue-400/10",
    icon: Clock,
  },
  processing: {
    label: "Processando",
    color: "text-amber-400 bg-amber-400/10",
    icon: Loader2,
  },
  generating_image: {
    label: "Gerando imagem",
    color: "text-amber-400 bg-amber-400/10",
    icon: Loader2,
  },
  generating_video: {
    label: "Gerando vídeos",
    color: "text-amber-400 bg-amber-400/10",
    icon: Loader2,
  },
  finished: {
    label: "Finalizada",
    color: "text-primary bg-primary/10",
    icon: CheckCircle2,
  },
  expired: {
    label: "Expirada",
    color: "text-muted-foreground bg-muted",
    icon: AlertTriangle,
  },
  error: {
    label: "Erro",
    color: "text-destructive bg-destructive/10",
    icon: XCircle,
  },
};

const getStatusConfig = (status: string) => {
  return statusConfig[status] || {
    label: status,
    color: "text-muted-foreground bg-muted",
    icon: Clock,
  };
};

export const TaskCard = forwardRef<HTMLDivElement, TaskCardProps>(
  function TaskCard({ task, onDelete, onDuplicate }, ref) {
    const status = getStatusConfig(task.status);
    const StatusIcon = status.icon;
    const videoCount = getVideoCount(task);
    const thumbnail = getTaskThumbnail(task);

    const handleDownload = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // TODO: Implement real download
      alert("Download simulado iniciado!");
    };

    const handleDuplicate = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDuplicate(task);
    };

    const handleDelete = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDelete(task.id);
    };

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        layout
      >
        <Card className="overflow-hidden hover:border-primary/50 transition-all group">
          <div className="flex flex-col sm:flex-row">
            {/* Thumbnail - Fixed size */}
            <div className="relative w-full sm:w-48 h-48 flex-shrink-0 bg-secondary">
              <img
                src={thumbnail}
                alt={task.product_name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-background/80 backdrop-blur-sm rounded-full text-xs">
                <Video className="w-3 h-3" />
                {videoCount} vídeo{videoCount !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-4 flex flex-col">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h3 className="font-semibold text-lg mb-1">{task.product_name}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {task.product_description || "Sem descrição"}
                  </p>
                </div>
                <div
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${status.color}`}
                >
                  <StatusIcon
                    className={`w-3.5 h-3.5 ${
                      task.status === "processing" ? "animate-spin" : ""
                    }`}
                  />
                  {status.label}
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                <span>Criada em: {formatDate(task.created_at)}</span>
                {task.expires_at && (
                  <>
                    <span>•</span>
                    <span>Expira em: {formatDate(task.expires_at)}</span>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="mt-auto flex items-center gap-2 flex-wrap">
                <Link to={`/dashboard/task/${task.id}`}>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Eye className="w-4 h-4" />
                    Ver detalhes
                  </Button>
                </Link>

                {task.status === "finished" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={handleDownload}
                  >
                    <Download className="w-4 h-4" />
                    Baixar
                  </Button>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={handleDuplicate}
                >
                  <Copy className="w-4 h-4" />
                  Duplicar
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-destructive hover:text-destructive"
                  onClick={handleDelete}
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    );
  }
);
